/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useModal } from '@utils/hooks';

import { PhoneFormatsType } from '@modules/global/types';

import Input from '@components/Input';
import Modal from '@components/NewModal';

import CloseIcon from './icon_close.svg';
import SearchIcon from './search.svg';

import style from './CountrySelect.scss';

const cn = bemCssModules(style, 'CountrySelect');
const ti = getTestId('CountrySelect');

const createBold = (str: string, substr: string): React.ReactNode => {
  const index = str.toLowerCase().indexOf(substr.toLowerCase());
  const isMatch = substr && index !== -1;
  if (!isMatch) return str;
  return [
    str.substring(0, index),
    <b key={index}>{str.substring(index, index + substr.length)}</b>,
    str.substring(index + substr.length),
  ];
};

const filter = (search: string, country: CountryType) => {
  const title = country.title.toLowerCase();
  const phonePrefix = `+${country.phone_prefix.toLowerCase()}`;
  const lowerSearch = search.toLowerCase();
  const searchSubStrings = lowerSearch.trim().split(' ');
  const fullCountryStr = `${title}${phonePrefix}`;

  return (
    title.includes(lowerSearch) ||
    phonePrefix.includes(lowerSearch) ||
    searchSubStrings.every(str => fullCountryStr.includes(str))
  );
};

export type CountryType = {
  code: PhoneFormatsType;
  title: string;
  phone_prefix: string;
};

export type TCountrySelect = {
  className?: string;
  classNameButton?: string;
  param?: keyof CountryType;
  value: string;
  placeholder?: string;
  label?: string;
  countries: CountryType[];
  onSelectCountry: (country: CountryType['code']) => void;
  onModalOpen?: () => void;
};

export const CountrySelect: React.FC<TCountrySelect> = props => {
  const {
    className,
    classNameButton,
    onSelectCountry,
    onModalOpen,
    value,
    label,
    countries,
    placeholder,
    param = 'phone_prefix',
  } = props;

  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLLabelElement>(null);
  const [isOpen, setIsOpen, modalRef] = useModal([inputRef, containerRef]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      onModalOpen?.();
    }
  }, [isOpen]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value);

  const toggleModal = () => {
    if (!isOpen) setSearch('');
    setIsOpen(!isOpen);
  };

  const handleClick = (code: CountryType['code']) => () => {
    onSelectCountry(code);
    setIsOpen(false);
  };

  const getIndex = (str: string) => str.toLowerCase().indexOf(search.toLowerCase());
  const sort = (el1: CountryType, el2: CountryType) => getIndex(el1.title) - getIndex(el2.title);

  const content = countries
    .filter(v => filter(search, v))
    .sort(sort)
    .map(country => (
      <button
        key={country.code}
        className={cn('option')}
        type="button"
        onClick={handleClick(country.code)}
      >
        <span className={cn('title')}>{createBold(country.title, search)}</span>
        <b>{createBold(`+${country.phone_prefix}`, search)}</b>
      </button>
    ));

  const countryValue = value && countries.find(country => country.code === value)?.[param];
  const prefix = param === 'phone_prefix' ? '+' : '';

  return (
    <div ref={modalRef} className={cn(null, [className])}>
      <label>
        <div className={cn('label')}>{label}</div>
        <div className={cn('value', { title: param === 'title' })}>
          <button
            className={cn('button', { empty: !value, hidden: isOpen }, [classNameButton])}
            type="button"
            data-testid={ti('button')}
            onClick={toggleModal}
          >
            {prefix}
            {countryValue}
          </button>
          <input
            ref={inputRef}
            data-hash="input"
            placeholder={(param === 'title' && placeholder) || ''}
            className={cn('input', { hidden: !isOpen }, ['text-base'])}
            type="search"
            value={search}
            data-testid={ti('input')}
            onChange={handleSearch}
          />
        </div>
      </label>
      <Modal
        className={cn('modal')}
        isOpen={isOpen}
        onClose={toggleModal}
        notPortal
        hideCloseButton
        hideMobileCloseButton
        data-testid={ti('dropdown')}
      >
        <label className={cn('input-block')} ref={containerRef}>
          <SearchIcon />
          <Input value={search} onChange={handleSearch} placeholder={placeholder} />
          <button className={cn('close-button')} type="button" onClick={toggleModal}>
            <CloseIcon />
          </button>
        </label>
        <div className={cn('content-block')}>
          {content}
          <div className={cn('not-support')}>{t('country-select.not-support')}</div>
        </div>
      </Modal>
    </div>
  );
};

export default CountrySelect;
